import React, { useEffect, useState } from "react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { BookingModal } from "@/components/BookingModal";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Loader2, CalendarDays } from "lucide-react";
import api from "@/lib/api";
import { SERVICES } from "@/lib/services";

const metaCls = {
  open: "bg-white border-amber-200 hover:border-[#E65100]",
  available: "bg-emerald-50 border-emerald-300 hover:border-emerald-500",
  limited: "bg-amber-50 border-amber-400 hover:border-amber-600",
  soldout: "bg-red-50 border-red-300",
};

export default function BookingCalendar() {
  const [serviceId, setServiceId] = useState(SERVICES[0].id);
  const [days, setDays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sel, setSel] = useState(null);
  const [open, setOpen] = useState(false);

  const service = SERVICES.find((s) => s.id === serviceId);

  useEffect(() => {
    let active = true;
    setLoading(true);
    api.get("/calendar", { params: { service_id: serviceId, days: 90 } })
      .then(({ data }) => { if (active) setDays(data); })
      .catch(() => { if (active) setDays([]); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [serviceId]);

  const openBooking = (d) => { setSel({ service, date: d.date }); setOpen(true); };
  const fmtDay = (iso) => {
    const dt = new Date(iso + "T00:00:00");
    return { d: dt.getDate(), m: dt.toLocaleDateString("en-US", { month: "short" }), w: dt.toLocaleDateString("en-US", { weekday: "short" }) };
  };

  return (
    <div className="App min-h-screen flex flex-col">
      <Header />
      <main className="flex-1 max-w-6xl mx-auto w-full px-4 py-10">
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-[#E65100] font-bold"><CalendarDays className="w-4 h-4" /> Plan Your Visit</div>
          <h1 className="font-cinzel text-3xl font-bold text-amber-950 mt-1">90-Day Booking Calendar</h1>
          <p className="text-slate-500 text-sm mt-1">Dates open for booking over the next 90 days. Tap a date to book instantly.</p>
          <div className="sacred-divider w-40 mx-auto mt-4" />
        </div>

        <div className="max-w-sm mx-auto mb-6">
          <Select value={serviceId} onValueChange={setServiceId}>
            <SelectTrigger data-testid="calendar-service-select"><SelectValue /></SelectTrigger>
            <SelectContent>{SERVICES.filter((s) => !s.hidden).map((s) => <SelectItem key={s.id} value={s.id}>{s.short}</SelectItem>)}</SelectContent>
          </Select>
        </div>

        {loading ? (
          <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#E65100]" /></div>
        ) : (
          <div className="grid grid-cols-3 sm:grid-cols-5 md:grid-cols-7 lg:grid-cols-10 gap-2">
            {days.map((d) => {
              const f = fmtDay(d.date);
              return (
                <button key={d.date} onClick={() => openBooking(d)} data-testid={`cal-day-${d.date}`}
                  className={`rounded-lg border p-2 text-center transition ${metaCls[d.status] || metaCls.open}`}>
                  <div className="text-[10px] uppercase text-slate-400 font-semibold">{f.w}</div>
                  <div className="text-lg font-bold text-amber-950 leading-none">{f.d}</div>
                  <div className="text-[10px] text-slate-500">{f.m}</div>
                  {d.limit != null
                    ? <div className={`text-[10px] mt-1 font-semibold ${d.status === "soldout" ? "text-red-600" : d.status === "limited" ? "text-amber-700" : "text-emerald-700"}`}>{d.status === "soldout" ? "Sold out" : `${d.remaining} left`}</div>
                    : <div className="text-[10px] mt-1 font-semibold text-emerald-700">Open</div>}
                </button>
              );
            })}
          </div>
        )}
      </main>
      <BookingModal service={open ? sel?.service : null} initialDate={sel?.date} open={open} onClose={() => setOpen(false)} />
      <Footer />
    </div>
  );
}
